import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "./contexts/ThemeContext";
import { TouchOffsetProvider } from "./contexts/TouchOffsetContext";
import { lazy, Suspense, useState, useEffect, useRef } from "react";
import ProtectedRoute from "./components/ProtectedRoute";
import MobileNavigation from "./components/MobileNavigation";
import { isPwaMode } from "./utils/pwaUtils";
import { supabase } from "@/integrations/supabase/client";
import { PWAInstallDrawer } from "./components/pwa/PWAInstallDrawer";
import { OfflineIndicator } from "./components/pwa/OfflineIndicator";
import { PageTransition } from "./components/layout/PageTransition";
import "./App.css";
import { usePwaSessionKeepAlive } from "@/hooks/usePwaSessionKeepAlive";
import { logger } from "@/utils/logger";

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="flex flex-col items-center">
      <div className="h-8 w-8 rounded-full border-4 border-t-primary border-r-primary border-b-muted border-l-muted animate-spin"></div>
      <p className="mt-4 text-muted-foreground">Loading application...</p>
    </div>
  </div>
);

const ErrorFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="flex flex-col items-center p-6 max-w-md text-center">
      <div className="text-destructive text-3xl mb-4">⚠️</div>
      <h2 className="text-xl font-semibold mb-2">Something went wrong</h2>
      <p className="text-muted-foreground mb-4">
        There was an error loading resources. Please try refreshing the page.
      </p>
      <button
        onClick={() => {
          if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
              type: 'FULL_RESET'
            });
            setTimeout(() => window.location.reload(), 300);
          } else {
            window.location.reload();
          }
        }}
        className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
      >
        Refresh Page
      </button>
    </div>
  </div>
);

const lazyLoadWithRetry = (componentImport: any) => {
  return lazy(() => {
    let previousError: any = null;

    const loadComponent = () => componentImport().catch((error: any) => {
      logger.error("Failed to load component:", error);

      if (previousError && previousError.message === error.message) {
        logger.error("Repeated error loading component, falling back to error page");
        return import('./pages/ErrorPage').catch(() => ({
          default: () => <ErrorFallback />
        }));
      }

      previousError = error;

      if (error.message && error.message.includes('valid JavaScript MIME type')) {
        logger.error(`MIME type error detected. This may be due to a service worker caching issue or network problem.`);

        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          logger.info("Sending cache reset message to service worker");
          navigator.serviceWorker.controller.postMessage({
            type: 'CLEAR_AUTH_CACHE'
          });
        }
      }

      if (error.message && error.message.includes('Failed to fetch dynamically imported module')) {
        const moduleMatch = error.message.match(/https:\/\/.*\.js/);
        if (moduleMatch) {
          logger.error(`Failed to load module: ${moduleMatch[0]}`);
        }
      }

      return new Promise((resolve) => {
        logger.info("Waiting before retry...");
        setTimeout(() => {
          logger.info("Retrying component load...");
          componentImport()
            .then(resolve)
            .catch(() => {
              logger.error("Second attempt to load component failed, falling back to error page");
              import('./pages/ErrorPage')
                .then(resolve)
                .catch(() => {
                  resolve({
                    default: () => <ErrorFallback />
                  });
                });
            });
        }, 1500);
      });
    });

    return loadComponent();
  });
};

const LazyComponentWrapper = ({ component: Component }: { component: React.ComponentType }) => {
  useEffect(() => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'BYPASS_AUTH_REQUESTS'
      });
    }
  }, []);

  return (
    <PageTransition>
      <Component />
    </PageTransition>
  );
};

const Welcome = lazyLoadWithRetry(() => import("./pages/Welcome"));
const Index = lazyLoadWithRetry(() => import("./pages/Index"));
const SignUp = lazyLoadWithRetry(() => import("./pages/SignUp"));
const Dashboard = lazyLoadWithRetry(() => import("./pages/Dashboard"));
const Patients = lazyLoadWithRetry(() => import("./pages/Patients"));
const Reports = lazyLoadWithRetry(() => import("./pages/Reports"));
const PatientDetails = lazyLoadWithRetry(() => import("./pages/PatientDetails"));
const PatientImport = lazyLoadWithRetry(() => import("./pages/PatientImport"));
const Settings = lazyLoadWithRetry(() => import("./pages/Settings"));
const NotFound = lazyLoadWithRetry(() => import("./pages/NotFound"));
const Collaboration = lazyLoadWithRetry(() => import("./pages/Collaboration"));
const Forums = lazyLoadWithRetry(() => import("./pages/Forums"));
const ForumDetails = lazyLoadWithRetry(() => import("./pages/ForumDetails"));
const ErrorPage = lazyLoadWithRetry(() => import("./pages/ErrorPage"));
const PatientMaintenance = lazyLoadWithRetry(() => import("./pages/PatientMaintenance"));
const DocProtocol = lazyLoadWithRetry(() => import("./pages/DocProtocol"));
const TokenLogin = lazyLoadWithRetry(() => import("./pages/TokenLogin"));
const WaitingVerificationPage = lazyLoadWithRetry(() => import("./pages/WaitingVerificationPage"));
const ForgotPasswordPage = lazyLoadWithRetry(() => import("./pages/ForgotPasswordPage"));
const UpdatePasswordPage = lazyLoadWithRetry(() => import("./pages/UpdatePasswordPage"));
const PrivacyPolicy = lazyLoadWithRetry(() => import("./pages/legal/PrivacyPolicy"));
const TermsOfService = lazyLoadWithRetry(() => import("./pages/legal/TermsOfService"));
const CookiePolicy = lazyLoadWithRetry(() => import("./pages/CookiePolicy"));
import CookieEnforcementManager from "./components/privacy/CookieEnforcementManager";
import AnalyticsTracker from "./components/privacy/AnalyticsTracker";


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000),
      meta: {
        onError: (error) => {
          logger.error("Query error:", error);
        },
      },
    },
  },
});

// Animated Routes Component to handle AnimatePresence if needed globally, 
// but for now we wrap individual pages for simplicity
const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<Welcome />} />
      <Route path="/login" element={<LazyComponentWrapper component={Index} />} />
      <Route path="/signup" element={<LazyComponentWrapper component={SignUp} />} />
      <Route path="/token-login" element={<LazyComponentWrapper component={TokenLogin} />} />
      <Route path="/forgot-password" element={<LazyComponentWrapper component={ForgotPasswordPage} />} />
      <Route path="/update-password" element={<LazyComponentWrapper component={UpdatePasswordPage} />} />
      <Route path="/auth/waiting-verification" element={<LazyComponentWrapper component={WaitingVerificationPage} />} />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <LazyComponentWrapper component={Dashboard} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/patients"
        element={
          <ProtectedRoute>
            <LazyComponentWrapper component={Patients} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/patients/import"
        element={
          <ProtectedRoute>
            <LazyComponentWrapper component={PatientImport} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <LazyComponentWrapper component={Reports} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/patients/:patientId"
        element={
          <ProtectedRoute>
            <LazyComponentWrapper component={PatientDetails} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <LazyComponentWrapper component={Settings} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/collaboration"
        element={
          <ProtectedRoute>
            <LazyComponentWrapper component={Collaboration} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/forums"
        element={
          <ProtectedRoute>
            <LazyComponentWrapper component={Forums} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/forums/:forumId"
        element={
          <ProtectedRoute>
            <LazyComponentWrapper component={ForumDetails} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/maintenance"
        element={
          <ProtectedRoute>
            <LazyComponentWrapper component={PatientMaintenance} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/doc-protocol"
        element={
          <ProtectedRoute>
            <LazyComponentWrapper component={DocProtocol} />
          </ProtectedRoute>
        }
      />

      <Route path="/privacy-policy" element={<LazyComponentWrapper component={PrivacyPolicy} />} />
      <Route path="/cookie-policy" element={<LazyComponentWrapper component={CookiePolicy} />} />
      <Route path="/terms-of-service" element={<LazyComponentWrapper component={TermsOfService} />} />
      <Route path="/error" element={<ErrorPage />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

function App() {
  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const isClickableElementRef = useRef<boolean>(false);
  const isProcessingTouchRef = useRef<boolean>(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const isPwa = isPwaMode();

  useEffect(() => {
    // Sync cookie consent on load
    import("@/services/CookieConsentService").then(({ CookieConsentService }) => {
      CookieConsentService.syncWithDatabase();
    });

    // Also sync consent when auth state changes (e.g., user logs in)
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
        logger.info(`Auth event ${event} detected, syncing and starting heartbeat...`);
        import("@/services/CookieConsentService").then(({ CookieConsentService }) => {
          CookieConsentService.syncWithDatabase();
        });

        // Generate device ID if it doesn't exist (for OAuth logins)
        if (!localStorage.getItem('device_binding_id')) {
          // Simple device fingerprint based on user agent + screen resolution
          const fingerprint = btoa(
            `${navigator.userAgent}_${screen.width}x${screen.height}_${navigator.language}_${new Date().getTimezoneOffset()}`
          ).substring(0, 32);
          localStorage.setItem('device_binding_id', fingerprint);
          logger.info("[App] Generated device ID:", fingerprint);
        }

        // Start IP binding heartbeat
        import("@/utils/auth/sessionHeartbeat").then(({ startHeartbeat }) => {
          startHeartbeat();
          logger.info("[App] IP binding heartbeat started");
        });
      }

      if (event === 'SIGNED_OUT') {
        // Clear IP binding heartbeat
        import("@/utils/auth/sessionHeartbeat").then(({ clearHeartbeat }) => {
          clearHeartbeat();
          logger.info("[App] IP binding heartbeat cleared");
        });
      }
    });

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'RELOAD_PAGE') {
        window.location.reload();

        // Keep PWA session alive when app resumes from background
        usePwaSessionKeepAlive();
      }
    };

    const handleError = (event: ErrorEvent) => {
      logger.error("Global error handler caught:", event);

      const errorText = event.message || (event.error && event.error.message) || '';
      if (errorText.includes('MIME type') ||
        errorText.includes('import') ||
        errorText.includes('module') ||
        errorText.includes('chunk')) {

        setHasLoadError(true);

        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'FULL_RESET'
          });
        }
      }
    };

    window.addEventListener('error', handleError);

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    const updateViewport = () => {
      const viewport = document.querySelector('meta[name="viewport"]');
      if (!viewport) {
        const meta = document.createElement('meta');
        meta.name = 'viewport';
        meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover';
        document.head.appendChild(meta);
      } else {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover');
      }
    };

    updateViewport();

    const handleTouchStart = (e: TouchEvent) => {
      if (isProcessingTouchRef.current) return;

      const target = e.target as HTMLElement;

      const isInteractive =
        target.tagName === 'BUTTON' ||
        target.tagName === 'A' ||
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.hasAttribute('data-clickable') ||
        target.closest('button') ||
        target.closest('a') ||
        target.closest('input') ||
        target.closest('select') ||
        target.closest('textarea') ||
        target.closest('[data-clickable="true"]') ||
        target.role === 'button' ||
        target.closest('[role="button"]');

      isClickableElementRef.current = !!isInteractive;

      if (isInteractive) {
        // Add active state class for touch feedback
        target.classList.add('active-press');
      }

      if (isClickableElementRef.current) {
        return;
      }

      if (e.touches.length === 1) {
        touchStartXRef.current = e.touches[0].clientX;
        touchStartYRef.current = e.touches[0].clientY;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (isClickableElementRef.current) return;

      if (touchStartXRef.current === null) return;

      const touchMoveX = e.touches[0].clientX;
      const touchMoveY = e.touches[0].clientY;
      const diffX = touchMoveX - touchStartXRef.current;
      const diffY = touchMoveY - (touchStartYRef.current || 0);

      if (Math.abs(diffX) > 15 && Math.abs(diffX) > Math.abs(diffY) * 1.5) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      touchStartXRef.current = null;
      touchStartYRef.current = null;

      // Remove active state class
      const target = e.target as HTMLElement;
      target.classList.remove('active-press');
      const interactiveParent = target.closest('.active-press');
      if (interactiveParent) {
        interactiveParent.classList.remove('active-press');
      }

      setTimeout(() => {
        isClickableElementRef.current = false;
        isProcessingTouchRef.current = false;
      }, 100);
    };

    const handlePopState = (e: PopStateEvent) => {
      history.pushState(null, document.title, window.location.href);
    };

    if (typeof history !== 'undefined') {
      history.pushState(null, document.title, window.location.href);
    }

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('error', handleError);

      // Cleanup auth listener
      authListener.subscription.unsubscribe();

      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }

      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const contentWrapperClass = isPwa ? "pwa-content-wrapper" : "";

  if (hasLoadError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center p-6 max-w-md text-center">
          <div className="text-destructive text-3xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold mb-2">Application Loading Error</h2>
          <p className="text-muted-foreground mb-4">
            There was an error loading some application resources. This is often caused by a caching issue.
          </p>
          <button
            onClick={() => {
              if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
                navigator.serviceWorker.controller.postMessage({
                  type: 'FULL_RESET'
                });
                setTimeout(() => window.location.reload(), 300);
              } else {
                window.location.reload();
              }
            }}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Reload Application
          </button>
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TouchOffsetProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <CookieEnforcementManager />
            <PWAInstallDrawer />
            <OfflineIndicator />

            <BrowserRouter>
              <AnalyticsTracker />
              <main className="app min-h-screen flex flex-col">
                <div className="app-shell flex flex-col flex-1">
                  <div className={`app-shell-content flex-1 ${contentWrapperClass}`}>
                    <Suspense fallback={<LoadingFallback />}>
                      <AppRoutes />
                    </Suspense>
                  </div>

                  <nav><MobileNavigation /></nav>
                </div>

              </main>
            </BrowserRouter>
          </TooltipProvider>
        </TouchOffsetProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
